import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { FiDownload, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { icons } from '~/assets';
import Button from '~/components/button';
import Container from '~/components/container';
import Dialog from '~/components/dialog';
import Loading from '~/components/loading';
import Pagination from '~/components/pagination';
import useApi, { ApiMethod } from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';
import {
  DocumentSolicitation,
  DocumentSolicitationStatus,
  DownloadDocumentSolicitationResponse,
} from '~/services/files/documentSolicitation/types';
import Constants from '~/utils/constants';
import * as St from './styles';

const ITEMS_PER_PAGE = 10;
const DOCUMENT_TYPE = 'Pacote completo';

const formatCpf = (cpf?: string) => {
  const value = cpf?.replace(/\D/g, '');

  if (!value || value.length !== 11) return cpf || '-';

  return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatDate = (date?: string) => {
  const value = moment(date);
  return value.isValid() ? value.format('DD/MM/YYYY') : '-';
};

type StatusPresentation = {
  label: string;
  tone: 'processing' | 'available' | 'expired' | 'error';
};

const statusPresentation: Record<
  DocumentSolicitationStatus,
  StatusPresentation
> = {
  PROCESSANDO: { label: 'Em processamento', tone: 'processing' },
  AGUARDANDO_DOCUMENTOS: { label: 'Em processamento', tone: 'processing' },
  AGUARDANDO_ZIP: { label: 'Em processamento', tone: 'processing' },
  PROCESSANDO_ZIP: { label: 'Em processamento', tone: 'processing' },
  PROCESSADO: { label: 'Disponível', tone: 'available' },
  EXPIRANDO: { label: 'Expirado', tone: 'expired' },
  EXPIRADA: { label: 'Expirado', tone: 'expired' },
  ERRO: { label: 'Falha no reprocessamento', tone: 'error' },
};

const getPresentation = (
  solicitation: DocumentSolicitation,
): StatusPresentation => {
  if (solicitation.status === 'ERRO') {
    return statusPresentation.ERRO;
  }

  if (
    solicitation.dataExpiracao &&
    !moment(solicitation.dataExpiracao).isAfter(moment())
  ) {
    return { label: 'Expirado', tone: 'expired' };
  }

  return statusPresentation[solicitation.status] || statusPresentation.ERRO;
};

const getDownloadFallbackMessage = (status: number) => {
  if (status === 410) {
    return 'O arquivo expirou. Faça uma nova solicitação.';
  }
  if (status === 404) {
    return 'O arquivo ainda não está disponível para download.';
  }
  return 'Não foi possível gerar o link de download. Tente novamente.';
};

interface ReprocessActionProps {
  solicitation: DocumentSolicitation;
  onSuccess: (response: DocumentSolicitation) => void;
  onError: (message: string) => void;
}

const ReprocessAction = ({
  solicitation,
  onSuccess,
  onError,
}: ReprocessActionProps): JSX.Element => {
  const api = useApi<DocumentSolicitation>(
    Endpoints.files.documentSolicitationReprocess(solicitation.id),
    ApiMethod.POST,
  );

  const reprocess = async () => {
    const response = await api.callApi();

    if (response.status !== 202) {
      onError(
        response.mensagem ||
          'Não foi possível reprocessar a solicitação. Tente novamente.',
      );
      return;
    }

    onSuccess(
      response.data || {
        ...solicitation,
        status: 'AGUARDANDO_DOCUMENTOS',
      },
    );
  };

  return (
    <St.ReprocessButton
      type="button"
      aria-label="Reprocessar solicitação"
      title="Reprocessar solicitação"
      onClick={reprocess}
      disabled={api.loading}
    >
      <FiRefreshCw className={api.loading ? 'is-loading' : undefined} />
      <span>{api.loading ? 'Reprocessando...' : 'Reprocessar'}</span>
    </St.ReprocessButton>
  );
};

const DocumentSolicitationHistory = (): JSX.Element => {
  const history = useHistory();
  const [solicitations, setSolicitations] = useState<DocumentSolicitation[]>(
    [],
  );
  const [filter, setFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{
    open: boolean;
    message: string;
    success?: boolean;
  }>({ open: false, message: '' });

  const listApi = useApi<DocumentSolicitation[]>(
    Endpoints.files.documentSolicitations,
    ApiMethod.GET,
  );
  const downloadApi = useApi<DownloadDocumentSolicitationResponse>(
    Endpoints.files.documentSolicitationDownload,
    ApiMethod.POST,
  );

  const loadSolicitations = async () => {
    const response = await listApi.callApi();

    if (response.status === 200) {
      setSolicitations(Array.isArray(response.data) ? response.data : []);
      return;
    }

    setSolicitations([]);
    setDialog({
      open: true,
      message:
        response.mensagem ||
        'Não foi possível carregar o histórico de solicitações.',
    });
  };

  useEffect(() => {
    loadSolicitations();
  }, []);

  const filteredSolicitations = useMemo(() => {
    const normalizedFilter = filter.trim().toLocaleLowerCase('pt-BR');

    if (!normalizedFilter) return solicitations;

    return solicitations.filter((solicitation) => {
      const presentation = getPresentation(solicitation);
      const requestDate = formatDate(solicitation.dataSolicitacao);

      return [
        requestDate,
        DOCUMENT_TYPE,
        solicitation.cpf || '',
        solicitation.idSolicitante || '',
        solicitation.nomeSolicitante || '',
        presentation.label,
      ].some((value) =>
        value.toLocaleLowerCase('pt-BR').includes(normalizedFilter),
      );
    });
  }, [filter, solicitations]);

  const totalPages = Math.ceil(filteredSolicitations.length / ITEMS_PER_PAGE);
  const visibleSolicitations = filteredSolicitations.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const changeFilter = (value: string) => {
    setFilter(value);
    setCurrentPage(1);
  };

  const download = async (solicitation: DocumentSolicitation) => {
    const idSolicitante = localStorage.getItem(Constants.USER_ID);

    if (!idSolicitante) {
      setDialog({
        open: true,
        message: 'Não foi possível identificar o usuário solicitante.',
      });
      return;
    }

    setDownloadingId(solicitation.id);
    const response = await downloadApi.callApi({
      id: solicitation.id,
      idSolicitante,
    });
    setDownloadingId(null);

    if (response.status === 200 && response.data?.urlDownload) {
      const link = document.createElement('a');
      link.href = response.data.urlDownload;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    }

    setDialog({
      open: true,
      message: response.mensagem || getDownloadFallbackMessage(response.status),
    });
  };

  const handleReprocessSuccess = (
    solicitation: DocumentSolicitation,
    response: DocumentSolicitation,
  ) => {
    setSolicitations((current) =>
      current.map((item) =>
        item.id === solicitation.id
          ? {
              ...item,
              status: response.status || 'AGUARDANDO_DOCUMENTOS',
              dataAtualizacao: response.dataAtualizacao || item.dataAtualizacao,
            }
          : item,
      ),
    );
    setDialog({
      open: true,
      success: true,
      message: `A solicitação do CPF ${formatCpf(
        solicitation.cpf,
      )} foi enviada para reprocessamento.`,
    });
  };

  const renderAction = (
    solicitation: DocumentSolicitation,
    presentation: StatusPresentation,
  ) => {
    if (presentation.tone === 'available') {
      return (
        <St.DownloadButton
          type="button"
          aria-label="Baixar pacote de documentos"
          title="Baixar pacote de documentos"
          onClick={() => download(solicitation)}
          disabled={downloadingId === solicitation.id}
        >
          <FiDownload />
        </St.DownloadButton>
      );
    }

    if (presentation.tone === 'processing') {
      return <St.WaitText>Aguarde...</St.WaitText>;
    }

    if (presentation.tone === 'error') {
      return (
        <ReprocessAction
          solicitation={solicitation}
          onSuccess={(response) =>
            handleReprocessSuccess(solicitation, response)
          }
          onError={(message) =>
            setDialog({ open: true, success: false, message })
          }
        />
      );
    }

    return '-';
  };

  return (
    <>
      <Loading status={listApi.loading} />
      <Dialog
        icon={dialog.success ? icons.confirmation : icons.exclamation}
        open={dialog.open}
        positiveLabel="Entendi"
        positiveAction={() => setDialog({ open: false, message: '' })}
        message={dialog.message}
      />

      <Container>
        <St.Header>
          <div>
            <St.Title>Histórico de solicitações</St.Title>
            <St.Subtitle>
              Acompanhe o status dos seus pacotes de documentos solicitados.
            </St.Subtitle>
          </div>
          <Button fitContent onClick={() => history.push('/file/documents')}>
            <span>Nova solicitação</span>
          </Button>
        </St.Header>

        <St.Toolbar>
          <St.SearchBox>
            <FiSearch />
            <St.SearchInput
              value={filter}
              onChange={(event) => changeFilter(event.target.value)}
              placeholder="Filtrar por data, CPF, tipo, solicitante ou status..."
            />
          </St.SearchBox>
          <Button
            fitContent
            icon={<FiRefreshCw />}
            onClick={loadSolicitations}
            disabled={listApi.loading}
          >
            <span>Recarregar</span>
          </Button>
        </St.Toolbar>

        <St.TableCard>
          <St.Table>
            <thead>
              <tr>
                <th>Data da solicitação</th>
                <th>CPF</th>
                <th>Tipo de documento</th>
                <th>Solicitante</th>
                <th>Status</th>
                <th>Data da expiração</th>
                <th>Ação</th>
              </tr>
            </thead>
            <tbody>
              {visibleSolicitations.map((solicitation) => {
                const presentation = getPresentation(solicitation);

                return (
                  <tr key={solicitation.id}>
                    <td data-label="Data da solicitação">
                      {formatDate(solicitation.dataSolicitacao)}
                    </td>
                    <td data-label="CPF do cliente">
                      {formatCpf(solicitation.cpf)}
                    </td>
                    <td data-label="Tipo de documento">{DOCUMENT_TYPE}</td>
                    <td data-label="Solicitante">
                      {solicitation.nomeSolicitante ||
                        solicitation.idSolicitante ||
                        '-'}
                    </td>
                    <td data-label="Status">
                      <St.Status tone={presentation.tone}>
                        {presentation.label}
                      </St.Status>
                    </td>
                    <td data-label="Data da expiração">
                      {formatDate(solicitation.dataExpiracao)}
                    </td>
                    <td data-label="Ação">
                      {renderAction(solicitation, presentation)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </St.Table>

          {!listApi.loading && visibleSolicitations.length === 0 && (
            <St.EmptyState>Nenhuma solicitação encontrada.</St.EmptyState>
          )}
        </St.TableCard>

        <St.Footer>
          <St.Warning>
            <strong>Aviso importante:</strong>
            {
              ' Por motivos de segurança e otimização de armazenamento, os arquivos expiram automaticamente após '
            }
            <strong>5 dias</strong>
            {' da data da solicitação. Realize o download dentro deste prazo.'}
          </St.Warning>

          {totalPages > 1 && (
            <Pagination
              totalPages={totalPages}
              initialCurrentPage={currentPage}
              onCurrentPageChange={setCurrentPage}
            />
          )}
        </St.Footer>
      </Container>
    </>
  );
};

export default DocumentSolicitationHistory;
